import {isValidPhoneNumber} from "react-phone-number-input";
import {INPUT_TYPES} from "../constants/form";
import {EIN_NUMBER} from "../constants/pardot";
import {AddressObjectType} from "../types";

/**
 * helper method to validate form value based on type
 * @param {string} type
 * @param {boolean} required
 * @param {boolean} invisible
 * @param {string} fieldName
 * @param {string | AddressObjectType}
 *
 * @returns {Array<string>}
 */
export const validateFormField = (
  type: string,
  required: boolean,
  invisible: boolean,
  fieldName: string,
  value: string | AddressObjectType
): Array<string> => {
  if (invisible) {
    return [];
  }

  switch (type) {
    case INPUT_TYPES.tel:
      return phoneNumberValidator(value.toString());
    case INPUT_TYPES.email:
      return emailValidator(value.toString());
    case INPUT_TYPES.text:
      if (fieldName === EIN_NUMBER) {
        return taxIdValidator(value.toString(), required);
      }
      return textValidator(value.toString(), required);
    case INPUT_TYPES.address:
      return addressValidator(value, required);
    case INPUT_TYPES.mailingPhysicalAddress:
      return addressValidator(value, required);
    default:
      return defaultValidator(value.toString(), required);
  }
};

const errors = {
  isRequired: "Required",
  isInvalidPhoneNumber: "Please enter a valid phone number",
  isInvalidEINNumber: "Please enter a valid EIN number",
  isInvalidEmailAddress: "Please enter a valid email address",
  isInvalidResponse: "Please enter a valid response",
  isInvalidMailingAddress: "Mailing address is missing or invalid",
  isInvalidPhysicalAddress: "Physical address is missing or invalid",
};

/**
 *
 * @param {string} value
 * @param {boolean} isRequired
 * @return {Array<string>}
 */
const defaultValidator = (
  value: string | boolean,
  isRequired?: boolean
): Array<string> => {
  if (typeof value === "string") {
    return textValidator(value, isRequired || false);
  }
  if (!!isRequired === true && missingRequired(value)) {
    return [errors.isRequired];
  }

  return [];
};

const addressValidator = (
  value: string | AddressObjectType,
  isRequired?: boolean
) => {
  const addressErrors = [];
  if (!!isRequired === true) {
    if (typeof value === "string" && missingRequired(value)) {
      return [errors.isInvalidMailingAddress, errors.isInvalidPhysicalAddress];
    }

    const temp = value as AddressObjectType;

    if (temp?.mailingAddressSet === false) {
      addressErrors.push(errors.isInvalidMailingAddress);
    }
    if (temp?.physicalAddressSet === false) {
      addressErrors.push(errors.isInvalidPhysicalAddress);
    }
  }
  return addressErrors;
};

/**
 *
 * @param {string} value
 * @param {bollean} isRequired
 * @return {Array<string>}
 */
const textValidator = (value: string, isRequired: boolean): Array<string> => {
  if (!!isRequired === true && missingRequired(value))
    return [errors.isRequired];
  if (value != null && containsMaliciousCode(value))
    return [errors.isInvalidResponse];

  return [];
};

/**
 *
 * @param {string} value
 * @param {boolean} isRequired
 * @return {Array<string>}
 */
const taxIdValidator = (value: string, isRequired: boolean) => {
  if (!!isRequired === true && missingRequired(value))
    return [errors.isRequired];
  if (value != null && !isValidTaxIdNumber(value))
    return [errors.isInvalidEINNumber];

  return [];
};

/* TODO(tchen): We only check length of phone number for now.
  The google phone validation library will be added in the future to replace this.
*/
/**
 *
 * @param {string} phoneNumber
 * @return {Array<string>}
 */
const phoneNumberValidator = (phoneNumber: string): Array<string> => {
  if (phoneNumber === "") return [errors.isRequired];
  if (/[a-zA-Z]/g.test(phoneNumber) === true)
    return [errors.isInvalidPhoneNumber];
  if (!isValidPhoneNumber(phoneNumber)) return [errors.isInvalidPhoneNumber];

  return [];
};

const BLACKLIST_EMAIL_DOMAINS = ["armyspy.com"];

// This version returns the array of strings expected by formula-one validations
/**
 *
 * @param {string} email
 * @return {Array<string>}
 */
const emailValidator = (email: string): Array<string> => {
  if (!email) return [errors.isRequired];
  if (!isValidEmail(email)) return [errors.isInvalidEmailAddress];

  // split email to check for domain
  const temp = email.toLowerCase().split("@");

  if (BLACKLIST_EMAIL_DOMAINS.includes(temp[1]))
    return [errors.isInvalidEmailAddress];

  return [];
};

/**
 *
 * Tax IDs can be one of the following formats and must include any hyphens:
 * EIN: XX-XXXXXXXOO. Where X is a number and O is either a letter, a number, or underscore (_) or blank().
 * CAIN: XXXXXX-XXXXX.
 *
 * see getTaxIdTypeForEntity in:
 * https://github.flexport.io/flexport/flexport/blob/master/webpack/assets/javascripts/tools/clientTools.js
 */
/**
 *
 * @param {string} value
 * @return {boolean}
 */
const isValidTaxIdNumber = (value: string) => {
  const isEIN = /^\d{2}-\d{7}(\w{2})?$/;
  const isCAIN = /^\d{6}-\d{5}$/;
  return isEIN.test(value) || isCAIN.test(value);
};

// https://stackoverflow.com/questions/46155/how-to-validate-email-address-in-javascript

/**
 *
 * @param {string} email
 * @return {boolean}
 */
export const isValidEmail = (email: string): boolean => {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(email);
};

// https://community.broadcom.com/symantecenterprise/communities/community-home/librarydocuments/viewdocument?DocumentKey=001f5e09-88b4-4a9a-b310-4c20578eecf9&CommunityKey=1ecf5f55-9545-44d6-b0f4-4e4a7f5f5e68&tab=librarydocuments
// https://larrysteinle.com/2011/02/20/use-regular-expressions-to-detect-sql-code-injection/

/**
 *
 * @param {string} value
 * @return {boolean}
 */
const containsMaliciousCode = (value: string) => {
  // Checks and returns true if name is possible sql injection
  const sqlRe =
    /('(''|[^'])*')|(;)|(\b(ALTER|CREATE|DELETE|DROP|EXEC(UTE){0,1}|INSERT( +INTO){0,1}|MERGE|SELECT|UPDATE|UNION( +ALL){0,1})\b)/;
  // Checks and returns true if name is possible html injection
  // eslint-disable-next-line no-useless-escape
  const htmlRe = /((\%3C)|<)[^\n]+((\%3E)|>)/;
  return sqlRe.test(value.toUpperCase()) || htmlRe.test(value);
};

/**
 *
 * @param {string | boolean} value
 * @return {boolean}
 */
const missingRequired = (value: string | boolean) => {
  if (value == null || value === undefined) {
    return true;
  }

  if (!value) {
    return true;
  }

  if (typeof value === "string" && value.trim() === "") {
    return true;
  }

  return false;
};
